import type { ComponentType } from "react";
import {
  AtSign,
  Briefcase,
  Camera,
  Cloud,
  Code,
  Disc3,
  Globe,
  Heart,
  Image as ImageIcon,
  Link2,
  Mail,
  MapPin,
  Music,
  Phone,
  Rss,
  Star,
  UtensilsCrossed,
  Users,
  Video,
} from "lucide-react";

/**
 * Universal social & streaming link registry. Bands link Spotify/Bandcamp,
 * restaurants link Yelp/Google/DoorDash, consultants link LinkedIn — one shared
 * vocabulary drives the header/footer icon rows and the `social_links` block.
 *
 * Data is stored on `tenant.settings.socialLinks` as a `{ platformKey: url }`
 * map (backward compatible with the pre-existing footer field). Unknown keys
 * still render via {@link resolvePlatform}'s generic fallback, so no key is ever
 * dropped.
 */
export type SocialCategory = "streaming" | "social" | "local" | "web" | "contact";

export interface SocialPlatformMeta {
  key: string;
  label: string;
  category: SocialCategory;
  Icon: ComponentType<{ className?: string }>;
  /** Lower renders first; grouped by category (streaming → social → local → web/contact). */
  order: number;
}

const PLATFORMS: SocialPlatformMeta[] = [
  // Streaming — most relevant for bands/artists, so they lead.
  { key: "spotify", label: "Spotify", category: "streaming", Icon: Music, order: 10 },
  { key: "apple_music", label: "Apple Music", category: "streaming", Icon: Disc3, order: 11 },
  { key: "bandcamp", label: "Bandcamp", category: "streaming", Icon: Disc3, order: 12 },
  { key: "soundcloud", label: "SoundCloud", category: "streaming", Icon: Cloud, order: 13 },
  { key: "youtube", label: "YouTube", category: "streaming", Icon: Video, order: 14 },
  { key: "youtube_music", label: "YouTube Music", category: "streaming", Icon: Video, order: 15 },
  { key: "tiktok", label: "TikTok", category: "streaming", Icon: Music, order: 16 },
  { key: "twitch", label: "Twitch", category: "streaming", Icon: Video, order: 17 },
  // Social
  { key: "instagram", label: "Instagram", category: "social", Icon: Camera, order: 30 },
  { key: "facebook", label: "Facebook", category: "social", Icon: Users, order: 31 },
  { key: "x", label: "X", category: "social", Icon: AtSign, order: 32 },
  { key: "threads", label: "Threads", category: "social", Icon: AtSign, order: 33 },
  { key: "linkedin", label: "LinkedIn", category: "social", Icon: Briefcase, order: 34 },
  { key: "pinterest", label: "Pinterest", category: "social", Icon: ImageIcon, order: 35 },
  { key: "github", label: "GitHub", category: "social", Icon: Code, order: 36 },
  { key: "patreon", label: "Patreon", category: "social", Icon: Heart, order: 37 },
  // Local / reviews
  { key: "yelp", label: "Yelp", category: "local", Icon: Star, order: 50 },
  { key: "google_business", label: "Google", category: "local", Icon: MapPin, order: 51 },
  { key: "tripadvisor", label: "Tripadvisor", category: "local", Icon: MapPin, order: 52 },
  { key: "doordash", label: "DoorDash", category: "local", Icon: UtensilsCrossed, order: 53 },
  // Web / contact
  { key: "website", label: "Website", category: "web", Icon: Globe, order: 70 },
  { key: "rss", label: "RSS", category: "web", Icon: Rss, order: 71 },
  { key: "email", label: "Email", category: "contact", Icon: Mail, order: 72 },
  { key: "phone", label: "Phone", category: "contact", Icon: Phone, order: 73 },
];

/** Common aliases normalized to a canonical platform key. */
const ALIASES: Record<string, string> = {
  twitter: "x",
  "apple-music": "apple_music",
  applemusic: "apple_music",
  "youtube-music": "youtube_music",
  youtubemusic: "youtube_music",
  google: "google_business",
  "google-business": "google_business",
  web: "website",
  site: "website",
  mail: "email",
  tel: "phone",
};

const BY_KEY: Record<string, SocialPlatformMeta> = Object.fromEntries(
  PLATFORMS.map((p) => [p.key, p]),
);

/** Canonical, ordered list — used by the admin editor's platform picker. */
export const SOCIAL_PLATFORMS: SocialPlatformMeta[] = [...PLATFORMS].sort(
  (a, b) => a.order - b.order,
);

function normalizeKey(raw: string): string {
  const k = raw.trim().toLowerCase().replace(/\s+/g, "_");
  return ALIASES[k] ?? k;
}

/**
 * Resolve a stored platform key to its metadata. Unknown keys get a generic
 * link icon and a title-cased label so custom/unknown platforms still render.
 */
export function resolvePlatform(key: string): SocialPlatformMeta {
  const normalized = normalizeKey(key);
  const known = BY_KEY[normalized];
  if (known) return known;
  const label = key
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { key: normalized, label: label || key, category: "web", Icon: Link2, order: 999 };
}

/** Build a safe href for a platform value (mailto/tel for contact, https elsewhere). */
export function toHref(key: string, value: string): string {
  const v = value.trim();
  const normalized = normalizeKey(key);
  if (normalized === "email") return v.startsWith("mailto:") ? v : `mailto:${v}`;
  if (normalized === "phone") return v.startsWith("tel:") ? v : `tel:${v.replace(/[^\d+]/g, "")}`;
  if (/^https?:\/\//i.test(v) || v.startsWith("mailto:") || v.startsWith("tel:")) return v;
  return `https://${v}`;
}

export interface ResolvedSocialLink {
  key: string;
  label: string;
  href: string;
  category: SocialCategory;
  Icon: ComponentType<{ className?: string }>;
}

/**
 * Normalize the stored `{ key: url }` map into an ordered, resolved list ready
 * for rendering. Blank values are skipped.
 */
export function resolveSocialLinks(
  links: Record<string, string> | undefined | null,
): ResolvedSocialLink[] {
  if (!links) return [];
  return Object.entries(links)
    .filter(([, url]) => typeof url === "string" && url.trim().length > 0)
    .map(([key, url]) => {
      const meta = resolvePlatform(key);
      return {
        key: meta.key,
        label: meta.label,
        href: toHref(key, url),
        category: meta.category,
        Icon: meta.Icon,
      };
    })
    .sort((a, b) => resolvePlatform(a.key).order - resolvePlatform(b.key).order);
}
